import {
    Anchor,
    Avatar,
    Box,
    Icon,
    MediaObject,
    Paragraph,
    Popover,
} from '@zillow/constellation-10';
import { IconCheckmarkCircleOutline } from '@zillow/constellation-icons';
import { debounce } from 'lodash';
import React, { useState, useEffect } from 'react';

import type { Contributor } from '../../B2CPostTemplate.types';
import ContributorPopover from '../ContributorPopover/ContributorPopover';

// GWEB-55: at most 3 authors (post author + additional) are shown; the rest
// are dropped from the byline. Structured data still lists every author.
export const MAX_DISPLAYED_AUTHORS = 3;

export interface ContributorsCardProps {
    author: Contributor;
    additionalAuthors?: Contributor[];
    reviewers?: Contributor[];
    editors?: Contributor[];
}

// CMS user IDs are unique; names are not. Without an ID or link, the list
// position keeps namesakes apart.
const contributorKey = (c: Contributor, index: number) =>
    c.id ?? c.link ?? `${c.name}-${index}`;

// First and last initials, like Constellation's Avatar.FullName at lg and up
const twoInitials = (name: string) => {
    const parts = name.trim().split(/\s+/);
    const first = Array.from(parts[0])[0] ?? '';
    return parts.length > 1
        ? `${first}${Array.from(parts[parts.length - 1])[0]}`
        : first;
};

// "A", "A and B", "A, B, and C"
const joinNames = (names: React.ReactNode[]): React.ReactNode[] =>
    names.map((name, i) => (
        <React.Fragment key={i}>
            {i > 0 && names.length > 2 && ','}
            {i > 0 && i === names.length - 1 && ' and'}
            {i > 0 && ' '}
            {name}
        </React.Fragment>
    ));

const ContributorsCard: React.FC<ContributorsCardProps> = ({
    author,
    additionalAuthors = [],
    reviewers = [],
    editors = [],
}) => {
    // The 890px switch is not a Constellation breakpoint and has no Panda
    // equivalent, and it does not gate styling — it gates which *element* is
    // rendered (a real link on mobile vs. a popover trigger on desktop). So it
    // stays as JS rather than moving into a css media query.
    const [isMobile, setIsMobile] = useState<boolean>(false);

    useEffect(() => {
        const onResize = () => {
            setIsMobile(window.innerWidth < 890);
        };
        const debouncedOnResize = debounce(onResize, 100);

        setIsMobile(window.innerWidth < 890);
        // eslint-disable-next-line no-undef
        window.addEventListener('resize', debouncedOnResize);
        window.addEventListener('orientationchange', debouncedOnResize);

        return () => {
            // eslint-disable-next-line no-undef
            window.removeEventListener('resize', debouncedOnResize);
            window.removeEventListener('orientationchange', debouncedOnResize);
        };
    }, []);

    // Helper to render name with popover
    const renderName = (contributor: Contributor, id: string) => {
        if (isMobile) {
            if (!contributor.link) {
                return contributor.name;
            }
            return (
                <Anchor id={id} href={contributor.link} target="_blank">
                    {contributor.name}
                </Anchor>
            );
        }
        // v8's <Popper triggered={<Popover body={...} />}>{trigger}</Popper>
        // collapses into a single v10 <Popover>: the trigger moves from Popper's
        // children to the `trigger` prop, and the popover content that used to be
        // a separate <Popover> element becomes this element's `body`. v10's
        // shorthand expands to Root > (Trigger, Portal > Backdrop > Content >
        // (Header?, Divider?, Body, CloseButton, Arrow)), which is the same
        // composition v8 built from Popper + Popover + Dialog — including the
        // default close button, which v8's Dialog also rendered by default.
        //
        // Non-obvious behavioral notes:
        //
        // - `header={null}` is required, not optional: v10 types `header` as a
        //   required ReactNode. Passing null reproduces v8, where no `header` was
        //   given and the header section (and its divider) was omitted. v10 gates
        //   both on `header` being truthy, so null yields the v8 markup.
        //
        // - `placement` must be set. v8 Popper defaulted to 'bottom-start';
        //   v10 defaults to 'top-start', so leaving it off would flip the popover
        //   above the name.
        //
        // - Offset is intentionally left at v10's default of 22px. v8's default
        //   was a popper.js offset of [-20, 20] for '-start' placements, i.e. 20px
        //   main-axis distance plus 20px of negative cross-axis skidding. The 20px
        //   distance is within 2px of v10's 22px, and reproducing the cross-axis
        //   skid would desynchronize v10's arrow, which is positioned by Floating
        //   UI's arrow() middleware rather than being nudged along with the panel.
        //
        // - `modal` is left at v10's default (modal below the md breakpoint),
        //   which matches v8's `modal: { sm_lte: true }`. It is unreachable here
        //   either way: this branch only renders at >=890px.
        //
        // - Popover.Trigger is a Slot, so it merges the reference ref and all of
        //   Floating UI's handlers/ARIA (including aria-haspopup="dialog", which
        //   v8's TriggerButton also set) onto the Anchor below rather than adding
        //   a wrapper element. That matters because the trigger sits inline in a
        //   flowing <p>.
        return (
            <Popover
                placement="bottom-start"
                header={null}
                body={<ContributorPopover contributor={contributor} />}
                trigger={
                    // v8 rendered an href-less <Anchor onClick={preventDefault}>.
                    // The preventDefault is dropped because there is no longer a
                    // navigation to cancel and it would swallow the click Floating
                    // UI needs. role="button" + tabIndex make the trigger
                    // keyboard-reachable, which the v8 version was not: an <a>
                    // without href is not focusable, so Floating UI's (and v8's)
                    // keyboard handlers had nothing to bind to. Kept as an Anchor
                    // rather than a Button so the inline text layout inside the
                    // surrounding <p> is unchanged — and so existing
                    // queryByRole('link') assertions still hold.
                    <Anchor id={id} role="button" tabIndex={0}>
                        {contributor.name}
                    </Anchor>
                }
            />
        );
    };

    const authors = [author, ...additionalAuthors].slice(
        0,
        MAX_DISPLAYED_AUTHORS
    );

    return (
        <>
            {/* A single author keeps the date inline ("Written by X on
                {date}"), as before multi-author; with several authors the
                spec puts it on its own line above the byline. */}
            {author.date && authors.length > 1 && (
                <Paragraph
                    textStyle="body-sm"
                    css={{ color: 'text.subtle', marginBottom: 'tighter' }}
                >
                    {author.date}
                </Paragraph>
            )}
            {/* v8 MediaObject's `align`/`gutter` props are gone in v10; its Root
                recipe is { display: flex, alignItems: start, gap: default } and
                everything moves into css. align="center" → alignItems: 'center',
                and gutter="sm" (16px) is already v10's default gap. Every author
                gets an avatar (a photo, or initials when there is none), so the
                media slot is never empty. */}
            <MediaObject
                css={{ alignItems: 'center' }}
                media={
                    // Overlap per the multi-author spec: a 2px ring in the
                    // surface color (replacing v10's 1px inset ring), each
                    // avatar after the first pulled left (8px at md, 6px at
                    // sm), and earlier avatars stacked on top. All of it is CSS
                    // rather than isMobile state so the server render already
                    // has the right size and nothing shifts after hydration.
                    // Per the spec, desktop and tablet use md and only mobile
                    // (below Constellation's lg breakpoint) drops to sm. This
                    // is a width/height override rather than Avatar's
                    // responsive size prop: our Panda build doesn't generate
                    // the conditional recipe classes for it (CT catches this).
                    <Box
                        css={{
                            display: 'flex',
                            alignItems: 'center',
                            '& > [data-c11n-component="Avatar.Root"]:not(:only-child)':
                                {
                                    boxShadow:
                                        '0 0 0 {borderWidths.thick} {colors.bg.neutral}',
                                },
                            '& > [data-c11n-component="Avatar.Root"] + [data-c11n-component="Avatar.Root"]':
                                { marginLeft: '-tighter' },
                            // One rule per slot, up to MAX_DISPLAYED_AUTHORS
                            '& > :nth-child(1)': { zIndex: 3 },
                            '& > :nth-child(2)': { zIndex: 2 },
                            '& > :nth-child(3)': { zIndex: 1 },
                            lgDown: {
                                '& > [data-c11n-component="Avatar.Root"]': {
                                    width: 'obj.sm',
                                    height: 'obj.sm',
                                },
                                '& > [data-c11n-component="Avatar.Root"] + [data-c11n-component="Avatar.Root"]':
                                    { marginLeft: '-150' },
                            },
                        }}
                    >
                        {authors.map((a, i) => (
                            // v8 wrapped Avatar in <Box as={Avatar} sx={...}>; v10
                            // Avatar takes css directly, so the Box is unnecessary.
                            //
                            // The image goes through src/alt rather than an <Image>
                            // child: v10 Avatar only renders its internal
                            // Avatar.Image slot — the one carrying the circular
                            // clip — when children are empty. See AuthorAvatar.tsx.
                            // Without a photo, the children below render initials.
                            //
                            // overflow: 'hidden' restores a v8 behavior that v10
                            // dropped. v8's AvatarMixin set `overflow: hidden` on
                            // the Avatar root; v10's avatarRecipe base does not —
                            // it only clips inside the Avatar.Image slot. WordPress
                            // author avatars are frequently dead URLs, and a broken
                            // <img> renders the browser's fallback icon plus the
                            // full `alt` text, which is not clipped by the img's
                            // own overflow; without this the name spills out of
                            // the circle and overlaps the byline text.
                            <Avatar
                                key={contributorKey(a, i)}
                                size="md"
                                src={a.avatar}
                                alt={a.name}
                                tone="accent-1"
                                // Decorative: the byline right next to it names
                                // each author (Constellation Avatar a11y guidance)
                                aria-hidden="true"
                                css={{
                                    // The root stays inline-flex (v10's default)
                                    // so initials stay centered in the circle.
                                    overflow: 'hidden',
                                    boxShadow: 'none',
                                    // `display: block` on the img is the other
                                    // half of that broken-URL fix, and it also
                                    // restores v8 sizing. A broken <img> with alt
                                    // text is a non-replaced *inline* box, and
                                    // width/height do not apply to those, so the
                                    // box grows to fit the alt text. v10's
                                    // Avatar.Image carries the `c11n` class, and
                                    // `.c11n { all: unset }` resets this repo's
                                    // globalCss `img { max-width: 100% }`.
                                    // Blockifying the img makes width/height apply
                                    // again, matching the `& img` override in
                                    // Upsell.tsx. No effect once the image loads.
                                    '& img': {
                                        display: 'block',
                                        inlineSize: '100%',
                                        blockSize: '100%',
                                    },
                                }}
                            >
                                {/* Design wants two initials while the avatar
                                    is md and one at sm. Avatar.FullName picks
                                    initials from the non-responsive size prop
                                    (one at md), so both are rendered and the
                                    size override's breakpoint picks one.
                                    undefined, not false: any other child makes
                                    Avatar drop its src image. */}
                                {a.avatar ? undefined : (
                                    <>
                                        <Box
                                            css={{
                                                lgDown: { display: 'none' },
                                            }}
                                        >
                                            {twoInitials(a.name)}
                                        </Box>
                                        <Avatar.FullName
                                            css={{
                                                display: 'none',
                                                lgDown: { display: 'block' },
                                            }}
                                        >
                                            {a.name}
                                        </Avatar.FullName>
                                    </>
                                )}
                            </Avatar>
                        ))}
                    </Box>
                }
            >
                <>
                    {/* v8 fontType="bodySmall" (14px) → textStyle="body-sm";
                        fontColor="textMedium" (#535364) → text.subtle (gray.600,
                        same hex). Style props are gone in v10, so color moves
                        into css. */}
                    <Paragraph
                        textStyle="body-sm"
                        css={{ color: 'text.subtle' }}
                    >
                        Written by{' '}
                        {joinNames(
                            authors.map((a, i) =>
                                renderName(a, `author-${contributorKey(a, i)}`)
                            )
                        )}
                        {author.date && authors.length === 1 && (
                            <> on {author.date}</>
                        )}
                    </Paragraph>
                    {(!!reviewers.length || !!editors.length) && (
                        // v8 align="top" mapped to flex-start, which is already
                        // v10's default alignItems, so only the gutter needs
                        // translating. v8 gutter="NONE" was not a real spacing
                        // token — spaceMixin fell through to Number('NONE'), got
                        // NaN, and emitted 0px — so the v10 equivalent is
                        // gap: 'none'. Media is always present here (the
                        // checkmark), so no gap ternary is needed.
                        <MediaObject
                            css={{ gap: 'none' }}
                            media={
                                // v10 icons are raw SVGs with no size/fontColor/
                                // spacing props, so the icon is wrapped in v10
                                // <Icon> and styled through css (v8 achieved the
                                // same with <Box as={Icon}>).
                                //
                                // Icon sizes shifted one step between versions:
                                // v8 size="xs" is 16px and v10's smallest fixed
                                // size, sm, is also 16px (v10 Icon has no "xs").
                                //
                                // v8 fontColor="borderGreen" is SkunkWorks
                                // #559E30. There is no exact v10 match;
                                // green.500 (#46A130) is the closest of any v10
                                // color — off by (15, 3, 0), vs. green.400
                                // #68C952 at ~58 and green.600 #196100 at ~98.
                                // Kept on the primitive so this accent does not
                                // invert in dark mode. Note the deliberate
                                // divergence from the /review-v10 known-mismatch
                                // table, which routes v8 #559E30 to the semantic
                                // text.accent.green.hero — that token IS
                                // green.600 #196100, a much darker green. The
                                // repo's COLOR_MAP header rejects semantic
                                // tokens for custom accents precisely because
                                // they "render dark in VR". Flagged for design
                                // sign-off rather than decided here.
                                //
                                // v8 $spacing.xxs (4px) → 'tightest'.
                                <Icon
                                    size="sm"
                                    css={{
                                        color: 'green.500',
                                        display: 'block',
                                        marginRight: 'tightest',
                                        marginTop: 'tightest',
                                    }}
                                >
                                    <IconCheckmarkCircleOutline />
                                </Icon>
                            }
                        >
                            <Paragraph
                                textStyle="body-sm"
                                css={{ color: 'text.subtle' }}
                            >
                                {!!reviewers.length && (
                                    <>
                                        Reviewed by{' '}
                                        {joinNames(
                                            reviewers.map((r, i) =>
                                                renderName(
                                                    r,
                                                    `reviewer-${contributorKey(
                                                        r,
                                                        i
                                                    )}`
                                                )
                                            )
                                        )}
                                    </>
                                )}
                                {!!reviewers.length && !!editors.length && ', '}
                                {!!editors.length && (
                                    <>
                                        Edited by{' '}
                                        {joinNames(
                                            editors.map((e, i) =>
                                                renderName(
                                                    e,
                                                    `editor-${contributorKey(
                                                        e,
                                                        i
                                                    )}`
                                                )
                                            )
                                        )}
                                    </>
                                )}
                            </Paragraph>
                        </MediaObject>
                    )}
                </>
            </MediaObject>
        </>
    );
};

export default ContributorsCard;
